"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { User, LogOut, LogIn, Trash2, Loader2, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

type SidebarState =
  | "expanded"
  | "collapsed"
  | "transition-opening"
  | "transition-closing";

interface AuthSectionProps {
  sidebarState: SidebarState;
  // Lets the mobile sidebar close itself before the modal opens.
  onAction?: () => void;
}

export function AuthSection({ sidebarState, onAction }: AuthSectionProps) {
  const { accountsEnabled, isLoading, user, isAuthenticated, logout, openAuthModal } =
    useAuth();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [logoutError, setLogoutError] = useState("");
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const showAuth =
    sidebarState === "expanded" || sidebarState === "transition-opening";
  const authDelay = sidebarState === "transition-opening" ? 0.15 : 0;
  const authDuration = sidebarState === "transition-closing" ? 0 : 0.15;

  if (!accountsEnabled || isLoading) return null;

  const handleLogout = async () => {
    setLogoutError("");
    setIsLoggingOut(true);
    const result = await logout();
    setIsLoggingOut(false);
    if (result.success) {
      onAction?.();
    } else {
      setLogoutError(result.error);
    }
  };

  return (
    <>
      <AnimatePresence>
        {showAuth && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, transition: { duration: authDuration } }}
            transition={{ duration: 0.15, delay: authDelay }}
            className="mt-auto px-4 py-3 bg-(--outer-bg) shrink-0 border-t border-border/40"
          >
            {isAuthenticated && user ? (
              <div className="space-y-1 w-full">
                <div className="flex items-center gap-2 px-3 py-2 bg-white/40 rounded-lg">
                  <User className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    {user.firstName && (
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.firstName}
                      </p>
                    )}
                    <p
                      className={
                        user.firstName
                          ? "text-xs text-muted-foreground truncate"
                          : "text-sm font-medium text-gray-900 truncate"
                      }
                    >
                      {user.email}
                    </p>
                  </div>
                </div>
                {logoutError && (
                  <p className="flex items-start gap-1.5 px-3 text-xs text-red-600">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>{logoutError}</span>
                  </p>
                )}
                <button
                  type="button"
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-900 hover:bg-white/50 rounded-lg transition-colors disabled:opacity-60"
                >
                  {isLoggingOut ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <LogOut className="h-4 w-4" />
                  )}
                  <span>Abmelden</span>
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50/60 rounded-lg transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Konto löschen</span>
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  onAction?.();
                  openAuthModal("login");
                }}
                className="w-full block text-left px-3 py-2.5 text-sm font-medium text-primary-600 hover:text-primary-700 bg-transparent hover:bg-primary-50 rounded-lg transition-all duration-200 group cursor-pointer"
              >
                <span className="flex items-center gap-2">
                  <LogIn className="h-4 w-4 group-hover:scale-110 transition-transform shrink-0" />
                  <span className="flex-1">Anmelden</span>
                </span>
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <DeleteAccountDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onDeleted={() => {
          setConfirmDelete(false);
          onAction?.();
        }}
      />
    </>
  );
}

function DeleteAccountDialog({
  isOpen,
  onClose,
  onDeleted,
}: {
  isOpen: boolean;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const { deleteAccount } = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState("");

  const handleClose = () => {
    if (isDeleting) return;
    setError("");
    onClose();
  };

  const handleDelete = async () => {
    setError("");
    setIsDeleting(true);
    const result = await deleteAccount();
    setIsDeleting(false);
    if (result.success) {
      onDeleted();
    } else {
      setError(result.error);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-70 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50"
            onClick={handleClose}
          />
          <motion.div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-account-title"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-sm bg-(--viewport-bg) rounded-2xl shadow-xl border border-border p-6"
          >
            <div className="flex items-start gap-3">
              <div className="shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h2 id="delete-account-title" className="text-lg font-semibold text-foreground">
                  Konto löschen?
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Ihr Konto und alle darin gespeicherten Chats werden endgültig gelöscht.
                  Das lässt sich nicht rückgängig machen.
                </p>
              </div>
            </div>

            {error && (
              <div role="alert" className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={handleClose}
                disabled={isDeleting}
                className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg bg-background hover:bg-muted text-foreground border border-border shadow-xs transition-colors"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-red-600 hover:bg-red-700 text-white shadow-xs transition-colors disabled:opacity-60"
              >
                {isDeleting && <Loader2 className="h-4 w-4 animate-spin" />}
                <span>Endgültig löschen</span>
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
